/**
 * LLD-XCC-210 / §24.2 rule 22 — the eight modals' own copy (`process/09-SCREEN-SPECS/m-*.md`),
 * sourced from Prototype_v2.html's `#m-*` markup. One file because, unlike the auth or onboarding
 * families, none of these eight shares a wizard or a layer — `_family-modals.md` is what binds
 * them, not a shared screen sequence.
 */

export const M_STEPUP_COPY = {
  heading: 'Confirm this restore',
  subheading: 'Write-order summary · typed confirmation of the destination',
  scopeNoteTemplate: (scope: string, destination: string) =>
    `${scope} to ${destination}. No restore ever deletes anything, and no restore ever overwrites code or history. Where a restore replaces a policy value, the previous value is recorded on the report so it can be put back.`,
  kindNoteTemplate: (kind: string) => `${kind} — one restore writes one kind of thing. Nothing outside it is touched by this job.`,
  destinationLabelTemplate: (destination: string) => `Type the destination workspace to confirm — ${destination}`,
  destinationMismatchNote: 'This must match the destination workspace exactly.',
  speedBumpLabel: "I'm not a robot",
  survivingNote:
    'Valid for five minutes and recorded in the audit log against the job. This does not re-run sign-in or your authenticator — proving who you are and confirming that you intend this write, at this scope, now, are two different checks. Changing anything on the previous steps asks for a new confirmation.',
  cancelButton: 'Cancel',
  confirmButton: 'Confirm and run',
} as const

export const M_RUNOK_COPY = {
  heading: 'Restore started',
  jobRunningTemplate: (handle: string) => `Job ${handle} is running`,
  emailNoteTemplate: (email: string) => `You can leave this page. We will email ${email} when it finishes.`,
  stayButton: 'Stay here',
  openJobButton: 'Open the job',
} as const

export const M_RPOINT_COPY = {
  heading: 'Choose a restore point',
  subheading: 'A restore point is a backup that exists — never a time you type',
  bannerNote:
    'Each row is one data snapshot, paired with the newest configuration snapshot at or before it. You pick the data snapshot; the pairing is ours and is shown so you can see what configuration comes with it. Anything committed after the row you pick was never captured.',
  columnData: 'Data snapshot',
  columnTrigger: 'Trigger',
  columnRepositories: 'Repositories',
  columnConfiguration: 'Paired configuration snapshot',
  columnExpires: 'Restorable until',
  noConfigurationNote: 'No configuration captured at or before this point.',
  partialNoteTemplate: (missing: number) => `Partial success — ${missing} repositories not reached. A real restore point for the rest; those are named before you select them.`,
  expiredNoteTemplate: (date: string) => `Retention expired ${date}. It is no longer restorable and cannot be selected. The stored objects were not deleted — nothing another backup relies on is lost.`,
  emptyNote: 'No restore points exist for this workspace yet.',
  cancelButton: 'Cancel',
  useButton: 'Use this restore point',
} as const

export const M_CANCELRESTORE_COPY = {
  heading: 'Cancel restore?',
  headingWithHandleTemplate: (handle: string) => `Cancel restore ${handle}?`,
  runningForTemplate: (kind: string, duration: string) => `${kind} · running ${duration}`,
  notUndoingWarning: 'Cancelling is not undoing. Everything this job has already written stays written. Nothing is rolled back, and nothing is deleted.',
  betweenObjectsNote: 'It stops between objects, never part-way through one. A write already sent is allowed to finish and is reported.',
  fullReportNote: 'You still get the full report. Every object this job did not reach is recorded as Skipped · cancelled, so a cancelled restore tells you exactly as much about what it did as a finished one.',
  protectionWriteNote:
    'This job writes protection rules, so one more thing is true. If it stops after a protection write, the destination is protected by a rule this job created — and no other protection-writing restore can run against these repositories until this one closes. Cancelling closes it.',
  outcomeNote: 'The job closes as Cancelled, which is its own outcome and is not counted as a failure — a person chose it. Owners, admins and operators may cancel; auditors may not.',
  reasonLabel: 'Reason, for the audit log (optional)',
  keepRunningButton: 'Keep running',
  cancelButton: 'Cancel this restore',
} as const

export const M_CANCEL_COPY = {
  headingTemplate: (handle: string) => `Cancel backup ${handle}?`,
  completedKeptTitle: 'The completed repositories are kept',
  completedKeptNote: 'They stay restorable. Cancelling never discards work already stored.',
  resumesTitle: 'The next run resumes where this stopped',
  resumesNote: 'Repositories already done are skipped.',
  closesTitle: 'The job closes as Cancelled',
  closesNote: "Tonight's recovery point objective will not be met until the next successful run.",
  reasonLabel: 'Reason, for the audit log (optional)',
  keepRunningButton: 'Keep running',
  cancelButton: 'Cancel this backup',
} as const

export const M_OPTGAP_COPY = {
  heading: 'Continue without an optional capability?',
  subheadingTemplate: (scope: string) => `${scope} was not granted. It is optional, so you can continue.`,
  lostTitle: 'What will not be backed up',
  lostNoteTemplate: (artefact: string, workspaceName: string) => `${artefact}, on every environment of every repository in ${workspaceName}. Everything else is unaffected.`,
  laterTitle: 'You can grant it later',
  laterNote: 'Add the scope in Atlassian and the next backup picks it up — every job re-checks scopes as its first step.',
  auditNote: 'This acknowledgement is written to the workspace audit log with your name and the time, so a customer who accepted reduced coverage can be told apart, months later, from one who was never asked.',
  backButton: 'Go back and fix it',
  continueButton: 'I understand, continue',
} as const

export const M_INVITE_COPY = {
  heading: 'Invite a user',
  subheading: 'They receive an email and choose their own password.',
  emailLabel: 'Email',
  roleLabel: 'Role',
  roleDescriptions: {
    ADMIN: '— settings, credential, restore',
    OPERATOR: '— run backups, run restores',
    AUDITOR: '— read only; exports require admin',
  } as const,
  grantsLabel: 'Workspace grants (Operator and Auditor only)',
  grantsNote:
    'Granted on acceptance, so scoping someone to two workspaces is one action rather than three. An Operator or Auditor invited with no grants can sign in and see nothing — legitimate, but rarely intended.',
  footerNote: 'Owner and Admin always reach every workspace, so grants do not apply to them. Two-factor authentication is required for every user, whatever their role, and is set up at first sign-in. The invite expires in 7 days.',
  cancelButton: 'Cancel',
  sendButton: 'Send invite',
} as const

export const M_MFARESET_COPY = {
  heading: 'Reset two-factor authentication',
  removalNote: 'This removes the authenticator on this account and signs the user out everywhere. They enrol a new one the next time they sign in — they cannot sign in at all until they do.',
  verifyFirstWarning: 'Verify who you are speaking to first, outside the product. A reset request arriving by email or chat is the shape an account takeover takes. The verification is the control here; this button is only what follows it.',
  auditNote: "This action is recorded in the organisation's audit log with your name against it.",
  outOfBandLabel: 'I have verified this person is who they claim to be, outside the product',
  cancelButton: 'Cancel',
  resetButton: 'Reset two-factor and sign this user out',
} as const
